/**
 * Which receipt line a scanned bag belongs on, when a product has more than one.
 *
 * A receipt can list the same product on several lines — Feed Manager booked one line per
 * expected batch until 2026-10-06, and anyone can type an order like that in Odoo. The screen
 * used to keep one running count per product and put every bag on the first line:
 *
 *  - the progress total added that product count once per line, so one bag read as 2
 *    (P00026: two lines of F0900564, 39 + 102);
 *  - every bag was committed to the first line, so it showed 80 received against 39 and the
 *    second line stayed at 0.
 *
 * Scans are deferred until Validate, so Odoo's own quantities do not move during a session and
 * the Worker cannot see which line has room. The screen can: it knows each line's expected and
 * done quantities and what has been scanned against each this session. So the allocation lives
 * here, counts are kept per line (move id), and each bag carries the line it was allocated to.
 *
 * Lines are filled in Odoo's order, each up to its expected quantity; anything beyond the
 * product's total lands on its last line, where the over-receipt warning already applies.
 */

/** The fields of a picking move this needs. */
export interface ReceiptLine {
  id: number;
  product: { id: number };
  expectedQty: number;
  doneQty: number;
}

/** A share of a scanned quantity, on one line. */
export interface LineShare {
  moveId: number;
  quantity: number;
}

/**
 * Split a scanned quantity across the product's lines.
 *
 * `sessionQty` is what this session has already put on each line, by move id. A serial bag is
 * a quantity of 1 and lands whole on one line; a counted lot entry can span lines. Returns an
 * empty list when the product is not on the receipt, so the caller keeps the Worker's move.
 */
export function allocateToLines(
  lines: readonly ReceiptLine[],
  sessionQty: Readonly<Record<number, number>>,
  productId: number,
  quantity: number,
): LineShare[] {
  const own = lines.filter((line) => line.product.id === productId);
  const last = own[own.length - 1];
  if (last === undefined || quantity <= 0) {
    return [];
  }

  const shares = new Map<number, number>();
  let left = quantity;
  for (const line of own) {
    if (left <= 0) break;
    const room = line.expectedQty - line.doneQty - (sessionQty[line.id] ?? 0);
    if (room <= 0) continue;
    const take = Math.min(room, left);
    shares.set(line.id, take);
    left -= take;
  }
  if (left > 0) {
    // More than the product's lines expect: an over-receipt, kept on its last line.
    shares.set(last.id, (shares.get(last.id) ?? 0) + left);
  }

  return own
    .filter((line) => shares.has(line.id))
    .map((line) => ({ moveId: line.id, quantity: shares.get(line.id) ?? 0 }));
}

/**
 * The product's expected and received quantities across all its lines, for the over-receipt
 * check. Comparing against one line's expected quantity warned "extra bag" on P00026 after 39
 * bags, when 141 were expected.
 */
export function productTotals(
  lines: readonly ReceiptLine[],
  sessionQty: Readonly<Record<number, number>>,
  productId: number,
): { expected: number; received: number } {
  let expected = 0;
  let received = 0;
  for (const line of lines) {
    if (line.product.id !== productId) continue;
    expected += line.expectedQty;
    received += line.doneQty + (sessionQty[line.id] ?? 0);
  }
  return { expected, received };
}
